import { Controller } from "stimulus";
import {
  debounce,
} from "../util";
import { formatCurrency } from "../util/currency";

export default class extends Controller {
  get MARKET_CAP() {
    return "market_cap";
  }

  get FDV() {
    return "fdv";
  }

  static targets = [
    "coinResultsListA",
    "coinResultsListB",
    "coinSearchInputA",
    "coinSearchInputB",
    "coinASymbol",
    "coinBSymbol",
    "coinAMcap",
    "coinBMcap",
    "coinAPrice",
    "ratio",
    "selectedMetric",
    "metricResult",
    "comparisonContainer",
    "comparisonResult",
    "resultCoinImage",
    "aErrorMessage",
    "bErrorMessage",
    "aClearInput",
    "bClearInput",
    "coinAMcapText",
    "coinAFDVText",
    "coinBMcapText",
    "coinBFDVText",
  ];

  static values = {
    noResults: String,
    marketCap: String,
    fdv: String,
  }

  connect() {
    const params = new URLSearchParams(window.location.search)
    this.debouncedSearchCoin = debounce(this.searchCoin.bind(this), 300);
    this.selectedACoin = ""
    this.selectedBCoin = ""
    this.metric = params.has("metric") && params.get("metric") === this.FDV ? this.FDV : this.MARKET_CAP
    this.AInputError = false
    this.BInputError = false

    this.tempCoin = ""
    this.tempSelectedCoin = ""

    this.initSelectedCoins()
  }

  changeSelectedCoin(e) {
    const searchInput = e.currentTarget.id === "a_input_container" ? "a" : "b"
    if (searchInput === "a" && this.coinSearchInputATarget.disabled && this.coinSearchInputATarget.value != "") {
      this.resetInput(null, "a")
    }

    if (searchInput === "b" && this.coinSearchInputBTarget.disabled && this.coinSearchInputBTarget.value != "") {
      this.resetInput(null, "b")
    }
  }

  initSelectedCoins() {
    const selectedCoinA = JSON.parse(this.coinSearchInputATarget.dataset.selectedCoin)
    this.selectedCoin(selectedCoinA, "a")

    const selectedCoinB = JSON.parse(this.coinSearchInputBTarget.dataset.selectedCoin)
    this.selectedCoin(selectedCoinB, "b")

    this.selectedMetricTarget.textContent = this.metric === this.MARKET_CAP ? this.marketCapValue : this.fdvValue
    this.metricResultTarget.textContent = this.metric === this.MARKET_CAP ? this.marketCapValue : this.fdvValue

    this.initValidateCoins()
    this.loadComparison()
  }

  initValidateCoins() {
    const inputAHasError = this.aErrorMessageTarget.textContent != ""
    const inputBHasError = this.bErrorMessageTarget.textContent != ""
    this.setHasError("a", inputAHasError)
    this.setHasError("b", inputBHasError)
    if (inputAHasError || inputBHasError) {
      this.comparisonContainerTarget.classList.add("tw-mt-24")
    }
  }

  selectMetric(metric) {
    this.metric = metric
    this.selectedMetricTarget.textContent = metric === this.MARKET_CAP ? this.marketCapValue : this.fdvValue
    this.metricResultTarget.textContent = metric === this.MARKET_CAP ? this.marketCapValue : this.fdvValue
    this.validateCoins()
    this.loadComparison()
  }

  selectFdvMetric() {
    this.selectMetric(this.FDV)
  }

  selectMcapMetric() {
    this.selectMetric(this.MARKET_CAP)
  }

  validateCoins() {
    this.clearErrors("a")
    this.clearErrors("b")
    if (this.validateCoin(this.selectedACoin, "a")) {
      this.clearErrors("a")
    }

    if (this.validateCoin(this.selectedBCoin, "b")) {
      this.clearErrors("b")
    }
  }

  select(e) {
    this.tempCoin = ""
    const searchInput = e.currentTarget.getAttribute("data-search-input")

    const coin = JSON.parse(e.currentTarget.getAttribute("data-coin"))

    const selectedCoinObj = {
      coinPrice: coin.price,
      coinSymbol: coin.symbol,
      coinImage: coin.thumbnail,
      coinMarketCap: coin.market_cap,
      coinMaxSupply: coin.max_supply,
      coinCirculatingSupply: coin.available_supply,
      coinFDV: coin.fully_diluted_valuation,
      coinSlug: coin.slug
    }
    searchInput === "a" ? this.selectedACoin = selectedCoinObj : this.selectedBCoin = selectedCoinObj

    this.selectInputChanges(searchInput, selectedCoinObj.coinImage, selectedCoinObj.coinSymbol, selectedCoinObj.coinPrice, coin.coinSlug)

    if (this.validateCoin(selectedCoinObj, searchInput)) {
      this.clearErrors(searchInput)
      this.loadComparison()
    } else {
      this.hideComparisonResult()
    }
  }

  validateCoin({coinMaxSupply, coinCirculatingSupply, coinSymbol}, searchInput) {
    if (this.metric == "market_cap") {
      if (coinCirculatingSupply == 0.0 ) {
        this.showError(coinSymbol, this.MARKET_CAP, searchInput)
        return false
      }
    } else if (this.metric == "fdv"){ // fdv error
      if (coinMaxSupply === "" || coinMaxSupply === null) {
        this.showError(coinSymbol, this.FDV, searchInput)
        return false
      }
    }
    return true
  }

  showError(symbol, errorType, searchInput) {
    document.getElementById(`${searchInput}_error_message`).textContent = ""
    this.comparisonContainerTarget.classList.add("tw-mt-24")
    this.showHideRedBorderInput(true, searchInput)
    this.setHasError(searchInput, true)
    let errorMessage
    if (errorType == "market_cap") {
      errorMessage = document.getElementById(`${searchInput}_market_cap_error`).getAttribute("data-error-text")
    } else if (errorType == "fdv") {
      errorMessage = document.getElementById(`${searchInput}_fdv_error`).getAttribute("data-error-text")
    }

    const finalError = errorMessage.replace("%{symbol}", symbol.toUpperCase())
    document.getElementById(`${searchInput}_error_container`).classList.remove("tw-hidden")
    document.getElementById(`${searchInput}_${errorType}_error`).innerHTML = finalError
  }

  showHideRedBorderInput(hasError, searchInput) {
    if (hasError) {
      if (searchInput === "a") {
        this.coinSearchInputATarget.classList.remove("tw-border-gray-300", "dark:tw-border-white", "dark:tw-border-opacity-12")
        this.coinSearchInputATarget.classList.add("tw-border-red-500", "dark:tw-border-red-500")
      } else {
        this.coinSearchInputBTarget.classList.remove("tw-border-gray-300", "dark:tw-border-white", "dark:tw-border-opacity-12")
        this.coinSearchInputBTarget.classList.add("tw-border-red-500", "dark:tw-border-red-500")
      }
    } else {
      if (searchInput === "a") {
        this.coinSearchInputATarget.classList.remove("tw-border-red-500", "dark:tw-border-red-500")
        this.coinSearchInputATarget.classList.add("tw-border-gray-300", "dark:tw-border-white", "dark:tw-border-opacity-12")
      } else {
        this.coinSearchInputBTarget.classList.remove("tw-border-red-500", "dark:tw-border-red-500")
        this.coinSearchInputBTarget.classList.add("tw-border-gray-300", "dark:tw-border-white", "dark:tw-border-opacity-12")
      }
    }
  }

  setHasError(searchInput, hasError) {
    searchInput == "a" ? this.AInputError = hasError : this.BInputError = hasError
  }

  clearErrors(searchInput) {
    this.setHasError(searchInput, false)
    this.showHideRedBorderInput(false, searchInput)
    this.comparisonContainerTarget.classList.remove("tw-mt-24")
    document.getElementById(`${searchInput}_market_cap_error`).textContent = ""
    document.getElementById(`${searchInput}_fdv_error`).textContent = ""
    document.getElementById(`${searchInput}_error_message`).textContent = ""
    document.getElementById(`${searchInput}_error_container`).classList.add("tw-hidden")
  }

  loadComparison() {
    if (this.selectedACoin != "" && this.selectedBCoin != "" && !this.AInputError && !this.BInputError) {
      this.appendQueryParams()
      this.comparisonResultTarget.classList.remove("tw-hidden")

      this.coinASymbolTargets[0].textContent = this.selectedACoin.coinSymbol
      this.coinBSymbolTargets[0].textContent = this.selectedBCoin.coinSymbol
      this.resultCoinImageTarget.src = this.selectedACoin.coinImage

      if (this.metric === this.MARKET_CAP) {
        this.generateMarketCapComparison()
      } else if (this.metric === this.FDV) {
        this.generateFDVComparison()
      }
    } else {
      this.hideComparisonResult()
    }
  }

  hideComparisonResult() {
    this.comparisonResultTarget.classList.add("tw-hidden")
  }

  generateMarketCapComparison() {
    const ratio = this.selectedBCoin.coinMarketCap / this.selectedACoin.coinMarketCap
    const priceOfA = this.selectedACoin.coinPrice * ratio

    this.coinASymbolTargets[1].textContent = `${this.selectedACoin.coinSymbol}`
    this.coinBSymbolTargets[1].textContent = `${this.selectedBCoin.coinSymbol}`
    this.coinAMcapTextTarget.classList.toggle('tw-hidden')
    this.coinBMcapTextTarget.classList.toggle('tw-hidden')
    this.coinAFDVTextTarget.classList.add('tw-hidden')
    this.coinBFDVTextTarget.classList.add('tw-hidden')

    this.coinAMcapTarget.textContent = formatCurrency(this.selectedACoin.coinMarketCap, "usd")
    this.coinBMcapTarget.textContent = formatCurrency(this.selectedBCoin.coinMarketCap, "usd")
    this.coinAPriceTarget.textContent = formatCurrency(priceOfA, "usd")
    this.ratioTarget.textContent = `(${ratio.toFixed(2)}x)`
    this.setRatioTextColor(ratio)
  }

  setRatioTextColor(ratio) {
    if (ratio >= 1) {
      this.ratioTarget.classList.add("tw-text-primary-500")
      this.ratioTarget.classList.remove("tw-text-red-500")
    } else {
      this.ratioTarget.classList.remove("tw-text-primary-500")
      this.ratioTarget.classList.add("tw-text-red-500")
    }
  }

  generateFDVComparison() {
    const coinAFdv = this.selectedACoin.coinFDV
    const coinBFdv = this.selectedBCoin.coinFDV
    const ratio = coinBFdv / coinAFdv
    const priceOfA = this.selectedACoin.coinPrice * ratio

    this.coinASymbolTargets[1].textContent = `${this.selectedACoin.coinSymbol}`
    this.coinBSymbolTargets[1].textContent = `${this.selectedBCoin.coinSymbol}`
    this.coinAMcapTextTarget.classList.add('tw-hidden')
    this.coinBMcapTextTarget.classList.add('tw-hidden')
    this.coinAFDVTextTarget.classList.toggle('tw-hidden')
    this.coinBFDVTextTarget.classList.toggle('tw-hidden')

    this.coinAMcapTarget.textContent = formatCurrency(coinAFdv, "usd")
    this.coinBMcapTarget.textContent = formatCurrency(coinBFdv, "usd")
    this.coinAPriceTarget.textContent = formatCurrency(priceOfA, "usd")
    this.ratioTarget.textContent = `(${ratio.toFixed(2)}x)`
  }

  selectedCoin(coin, selectedInput) {
    this.selectInputChanges(selectedInput, coin.thumbnail, coin.symbol, coin.price)
    const coinObject = {coinPrice: coin.price, coinSymbol: coin.symbol, coinImage: coin.thumbnail,
      coinMarketCap: coin.market_cap, coinFDV: coin.fully_diluted_valuation,
      coinMaxSupply: coin.max_supply, coinCirculatingSupply: coin.available_supply, coinSlug: coin.slug
    }
    selectedInput == "a" ? this.selectedACoin = coinObject : this.selectedBCoin = coinObject
  }

  selectInputChanges(searchInput, coinImage, coinSymbol, coinPrice) {
    this.setCoinImageThumbnail(coinImage, searchInput)
    this.setCoinSymbolAndPrice(coinSymbol, coinPrice, searchInput)
    this.setClearInputVisible(searchInput)
    this.disableInput(searchInput, true)
  }

  setCoinImageThumbnail(image, input) {
    if (input === "a") {
      this.removePaddingLeftA()
    } else {
      this.removePaddingLeftB()
    }

    this.setInputImage(input, image)
  }

  removePaddingLeftA() {
    this.coinSearchInputATarget.classList.remove("tw-pl-4")
    this.coinSearchInputATarget.classList.add("tw-pl-10")
  }

  removePaddingLeftB() {
    this.coinSearchInputBTarget.classList.remove("tw-pl-4")
    this.coinSearchInputBTarget.classList.add("tw-pl-10")
  }

  setCoinSymbolAndPrice(symbol, price, input) {
    const formattedSymbolPrice = `${symbol.toUpperCase()} ${formatCurrency(price, "usd")}`
    input === "a" ? this.coinSearchInputATarget.value = formattedSymbolPrice : this.coinSearchInputBTarget.value = formattedSymbolPrice
    this.toggleFontMedium(input)
  }

  toggleFontMedium(input) {
    input === "a" ? this.coinSearchInputATarget.classList.toggle("tw-font-medium") : this.coinSearchInputBTarget.classList.toggle("tw-font-medium")
  }

  setClearInputVisible(input) {
    document.getElementById(`${input}_clear_input`).classList.remove("tw-hidden")
  }

  setClearInputHidden(input) {
    document.getElementById(`${input}_clear_input`).classList.add("tw-hidden")
  }

  resetInput(e, softResetSearchInput) {
    const searchInput = softResetSearchInput || e.currentTarget.getAttribute("data-search-input")
    this.disableInput(searchInput, false)
    this.setClearInputHidden(searchInput)
    this.setInputImage(searchInput, "")
    this.toggleFontMedium(searchInput)

    if (softResetSearchInput) {
      this.tempCoin = this.coinSearchInputATarget.value
      this.tempSelectedCoin = this.selectedACoin
    }

    searchInput === "a" ? this.resetAInput(softResetSearchInput) : this.resetBInput(softResetSearchInput)
  }

  resetAInput(softResetSearchInput) {
    if (softResetSearchInput) {
      this.tempCoin = this.coinSearchInputATarget.value
      this.tempSelectedCoin = this.selectedACoin
    }

    this.coinSearchInputATarget.value = ""
    this.selectedACoin = null
    this.coinSearchInputATarget.classList.remove("tw-pl-10")
    this.coinSearchInputATarget.classList.add("tw-pl-4")
    this.coinSearchInputATarget.focus()
  }

  resetBInput(softResetSearchInput) {
    if (softResetSearchInput) {
      this.tempCoin = this.coinSearchInputBTarget.value
      this.tempSelectedCoin = this.selectedBCoin
    }

    this.coinSearchInputBTarget.value = ""
    this.selectedBCoin = null
    this.coinSearchInputBTarget.classList.remove("tw-pl-10")
    this.coinSearchInputBTarget.classList.add("tw-pl-4")
    this.coinSearchInputBTarget.focus()
  }

  disableInput(input, disable) {
    input === "a" ? this.coinSearchInputATarget.disabled = disable : this.coinSearchInputBTarget.disabled = disable
  }

  searchCoin(e) {
    this.setDropdownVisibility(e)

    const searchInput = e.target.dataset.searchInput;
    const searchPath = e.target.dataset.actionPath
    const coinSearchInput = e.target.value.trim();

    // Keep track of latest search query
    this._searchCount = this._searchCount || 0;
    this._searchCount += 1;
    const searchCount = this._searchCount;

    fetch(`${searchPath}?query=${coinSearchInput}`, {
      credentials: "same-origin"
    })
      .then(response => response.json())
      .then(res => {
        if (searchCount != this._searchCount) {
          // Old search result coming in late. Ignore.
          return;
        }

        if (res) {
          const coinsList = []
          res.coins.forEach((value) => {
            const item = `<div data-action="mousedown->crypto-compare#select"
            data-search-input=${searchInput}
            data-coin=${JSON.stringify(this.mapSearchResultsToCoin(value))}
            class="tw-cursor-pointer dark:hover:tw-bg-opacity-8 dark:hover:tw-bg-white dark:hover:tw-text-opacity-87
            dark:hover:tw-text-white dark:tw-text-opacity-87 dark:tw-text-white hover:tw-bg-gray-100 hover:tw-text-gray-700
            tw-flex tw-items-center tw-px-4 tw-py-2 tw-text-gray-700 tw-text-sm">
                <div class="tw-flex tw-w-full tw-inline-block tw-items-center">
                    <img class="tw-w-6" src="${value.thumbnail}" />
                    <div class="tw-ml-2 dark:tw-text-white-87 tw-text-gray-700">${value.name}</div>
                    <div class="tw-ml-1 dark:tw-text-white-60 tw-text-gray-500">${value.crypto_symbol.toUpperCase()}</div>
                </div>
              </div>`
            coinsList.push(item)
          })

          if (res.coins.length === 0) {
            coinsList.push(this.addNoResultsDropdownItem())
          }

          searchInput == "a" ? this.coinResultsListATarget.innerHTML = coinsList.join("") : this.coinResultsListBTarget.innerHTML = coinsList.join("")
        }
      });
  }

  addNoResultsDropdownItem() {
    return `<div class="dark:hover:tw-bg-opacity-8 dark:hover:tw-bg-white dark:hover:tw-text-opacity-87 dark:hover:tw-text-white dark:tw-text-opacity-87 dark:tw-text-white hover:tw-bg-gray-100 hover:tw-text-gray-700 tw-flex tw-items-center tw-px-4 tw-py-2 tw-text-gray-700 tw-text-sm">
              <div class="tw-flex tw-w-full tw-inline-block tw-items-center">
                  <div class="tw-ml-2 dark:tw-text-white-87 tw-text-gray-700">${this.noResultsValue}</div>
              </div>
            </div>`
  }

  setDropdownVisibility(e) {
    const searchInput = e.target.dataset.searchInput;
    const coinSearchInput = e.target.value.trim();

    const topCoinsDropdown = document.getElementById(`${searchInput}_top_coins`)
    const resultDropdown = document.getElementById(`${searchInput}_result`)

    if (coinSearchInput.length == 0) {
      topCoinsDropdown.classList.remove("tw-hidden")
      resultDropdown.classList.add("tw-hidden")
    } else {
      resultDropdown.classList.remove("tw-hidden")
      topCoinsDropdown.classList.add("tw-hidden")
    }
  }

  hideDropdowns(e) {
    const searchInput = e.target.dataset.searchInput;

    if (this.tempCoin && this.tempCoin != "") {
      searchInput === "a" ? this.restoreTempACoin() : this.restoreTempBCoin()
      this.tempCoin = ""
      this.tempSelectedCoin = {}
    }

    document.getElementById(`${searchInput}_top_coins`).classList.add("tw-hidden")
    document.getElementById(`${searchInput}_result`).classList.add("tw-hidden")
  }

  restoreTempACoin() {
    this.coinSearchInputATarget.value = this.tempCoin
    this.selectedACoin = this.tempSelectedCoin

    this.setInputImage("a", this.selectedACoin.coinImage)

    this.removePaddingLeftA()

    this.aClearInputTarget.classList.remove("tw-hidden")
    this.disableInput("a", true)
  }

  restoreTempBCoin() {
    this.coinSearchInputBTarget.value = this.tempCoin
    this.selectedBCoin = this.tempSelectedCoin

    this.setInputImage("b", this.selectedBCoin.coinImage)

    this.removePaddingLeftB()

    this.bClearInputTarget.classList.remove("tw-hidden")
    this.disableInput("b", true)
  }

  setInputImage(searchInput, image) {
    const imageThumbnail = document.getElementById(`${searchInput}_image`)
    imageThumbnail.src = image
  }

  mapSearchResultsToCoin(value) {
    const mappedObj = {
      price: value.price.usd, symbol: value.crypto_symbol.toUpperCase(),
      thumbnail: value.thumbnail, market_cap: value.market_cap.usd,
      max_supply: value.max_supply, available_supply: value.available_supply,
      fully_diluted_valuation: value.fully_diluted_valuation.usd, slug: value.slug
    }
    return mappedObj
  }

  appendQueryParams() {
    const arrayCoins = [this.selectedACoin.coinSlug, this.selectedBCoin.coinSlug]
    const currentURL = new URL(window.location.href.split("?")[0]);

    currentURL.searchParams.append("coins", arrayCoins);
    if (this.metric != this.MARKET_CAP) {
      currentURL.searchParams.append("metric", this.metric);
    }

    window.history.replaceState({},"",decodeURIComponent(currentURL));
  }
}
